import { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import {
  Loader2,
  ChevronDown,
  Save,
  Eye,
  Info,
} from 'lucide-react';

interface UserPreviewData {
  name: string;
  company: string;
  phone: string;
  email: string;
}

export function EmailSignatureSettings() {
  const { user } = useAuth();
  const { currentTenant } = useTenant();
  const [signatureEnabled, setSignatureEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [previewData, setPreviewData] = useState<UserPreviewData>({
    name: '',
    company: '',
    phone: '',
    email: '',
  });

  useEffect(() => {
    if (user && currentTenant?.tenant_id) {
      loadSettings();
      loadCurrentUserPreview();
    }
  }, [user, currentTenant?.tenant_id]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('tenants')
        .select('email_signature_enabled')
        .eq('tenant_id', currentTenant?.tenant_id)
        .single();

      if (error) throw error;

      setSignatureEnabled(data?.email_signature_enabled ?? false);
    } catch (error) {
      console.error('Error loading signature settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCurrentUserPreview = async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('first_name, last_name, email, phone_number')
        .eq('id', user?.id)
        .single();

      if (error) throw error;

      if (data) {
        const fullName = [data.first_name, data.last_name].filter(Boolean).join(' ');
        setPreviewData({
          name: fullName || '',
          company: currentTenant?.company_name || '',
          phone: data.phone_number || '',
          email: data.email || '',
        });
      }
    } catch (error) {
      console.error('Error loading user preview data:', error);
    }
  };

  const generateSignatureHTML = (userData: UserPreviewData): string => {
    const lines = [];

    if (userData.name) lines.push(`<div><strong>${userData.name}</strong></div>`);
    if (userData.company) lines.push(`<div>🏢 ${userData.company}</div>`);
    if (userData.phone) lines.push(`<div>📞 ${userData.phone}</div>`);
    if (userData.email) lines.push(`<div>✉️ <a href="mailto:${userData.email}">${userData.email}</a></div>`);

    return lines.join('');
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('tenants')
        .update({ email_signature_enabled: signatureEnabled })
        .eq('tenant_id', currentTenant?.tenant_id);

      if (error) throw error;

      toast.success('Email signature settings saved successfully');
    } catch (error) {
      console.error('Error saving signature settings:', error);
      toast.error('Failed to save email signature settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  const previewHTML = generateSignatureHTML(previewData);

  return (
    <Card>
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Email Signature</CardTitle>
                <CardDescription>
                  Enable automatic email signatures for all users in this tenant
                </CardDescription>
              </div>
              <ChevronDown
                className={cn(
                  'h-5 w-5 text-muted-foreground transition-transform duration-200',
                  isExpanded && 'rotate-180'
                )}
              />
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-6 pt-0">
            {/* Tenant-wide Toggle */}
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="space-y-1">
                <Label htmlFor="signature-enabled" className="text-sm font-medium">
                  Enable signatures for all users
                </Label>
                <p className="text-sm text-muted-foreground">
                  When enabled, all users will have signatures automatically added to emails
                </p>
              </div>
              <Switch
                id="signature-enabled"
                checked={signatureEnabled}
                onCheckedChange={setSignatureEnabled}
              />
            </div>

            {/* Info Alert */}
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription className="text-sm">
                <strong>Tenant-wide setting:</strong> This applies to all users in your organization.
                Each user's signature will automatically include their name, email, phone, and company from their user profile.
              </AlertDescription>
            </Alert>

            {/* Preview Section */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Eye className="h-4 w-4" />
                <Label className="text-sm font-medium">Preview (Your Signature)</Label>
              </div>

              {/* Current User Data Display */}
              <div className="p-3 bg-muted/30 rounded-lg space-y-2 text-sm">
                <p className="text-muted-foreground text-xs">
                  Example showing how your signature will appear:
                </p>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground text-xs ml-2">
                  <li><strong>Name:</strong> {previewData.name || '(Not set in user profile)'}</li>
                  <li><strong>Company:</strong> {previewData.company || '(Not set)'}</li>
                  <li><strong>Phone:</strong> {previewData.phone || '(Not set in user profile)'}</li>
                  <li><strong>Email:</strong> {previewData.email || '(Not set in user profile)'}</li>
                </ul>
              </div>

              {/* Visual Preview */}
              <div className="border rounded-lg p-4 bg-muted/20 min-h-[100px]">
                {previewHTML ? (
                  <div
                    className="prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewHTML, {
                      ALLOWED_TAGS: ['div', 'strong', 'a'],
                      ALLOWED_ATTR: ['href'],
                    }) }}
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No signature data available. User profile information is missing.
                  </p>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Users can update their name, phone, and email in Settings → Users. Company name comes from tenant settings.
              </p>
            </div>

            {/* Save Button */}
            <div className="flex gap-2">
              <Button onClick={saveSettings} disabled={saving}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                <Save className="mr-2 h-4 w-4" />
                Save Signature Settings
              </Button>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
